import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import { CloseOutlined, ReloadOutlined, CopyOutlined } from '@ant-design/icons';
import InputsSection from './components/InputsSection';
import PromptsSection from './components/PromptsSection';
import ParamsViewer from './components/ParamsViewer';
import ErrorDisplay from './components/ErrorDisplay';
import ImageViewerModal from '../../screens/app/zenScreen/components/ImageViewerModal';
import {
  extractInputImages,
  extractOutputResults,
  extractPrompts,
  getJobId,
  getErrorInfo,
  STATUS_COLORS,
  formatRelativeDate,
  formatDuration,
  formatCost,
  copyToClipboard,
  downloadImage,
} from './utils';
import './JobDetailView.css';

const JobDetailView = ({
  data,
  onClose,
  onReuseParams,
  onEditImage,
  onToggleLike,
  isMobile = false,
}) => {
  const [fullViewerImage, setFullViewerImage] = useState(null);
  const [fullViewerImages, setFullViewerImages] = useState([]);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);
  const [liked, setLiked] = useState(data?.is_liked || false);
  const [liking, setLiking] = useState(false);

  // Extract data
  const jobId = useMemo(() => getJobId(data), [data]);
  const inputImages = useMemo(() => extractInputImages(data), [data]);
  const outputs = useMemo(() => extractOutputResults(data), [data]);
  const prompts = useMemo(() => extractPrompts(data), [data]);
  const errorInfo = useMemo(() => getErrorInfo(data), [data]);

  // Get outputs by type
  const imageOutputs = useMemo(() => outputs.filter(o => o.type === 'image' && o.url), [outputs]);
  const videoOutputs = useMemo(() => outputs.filter(o => o.type === 'video' && o.url), [outputs]);
  const allMediaOutputs = useMemo(() => [...imageOutputs, ...videoOutputs], [imageOutputs, videoOutputs]);

  // All images for viewer — inputs are filtered to actual images, since the
  // viewer renders <img> and inputs can now be video or audio.
  const allImages = useMemo(
    () => [...inputImages.filter(i => i.type === 'image'), ...imageOutputs],
    [inputImages, imageOutputs]
  );

  // Status info
  const status = data?.status || 'unknown';
  const statusColor = STATUS_COLORS[status] || '#6b7280';
  const modelName = data?.model_name || data?.model?.model_name || data?.workflow_definition_name || data?.workflow?.name || 'Unknown';

  // Reset state when data changes
  useEffect(() => {
    setFullViewerImage(null);
    setCurrentOutputIndex(0);
    setLiked(data?.is_liked || false);
  }, [data]);

  const handleImageClick = useCallback((image, images = allImages) => {
    setFullViewerImage(image);
    setFullViewerImages(images);
  }, [allImages]);

  // Swiper instance ref
  const swiperRef = React.useRef(null);

  // Arrow navigation using Swiper API
  const goToPrev = () => {
    if (swiperRef.current && currentOutputIndex > 0) {
      swiperRef.current.slidePrev();
    }
  };
  
  const goToNext = () => {
    if (swiperRef.current && currentOutputIndex < allMediaOutputs.length - 1) {
      swiperRef.current.slideNext();
    }
  };

  
  if (!data) return null;

  const isCompleted = data.status === 'completed';
  const isFailed = data.status === 'failed';
  const isProcessing = data.status === 'processing' || data.status === 'pending';
  const currentOutput = allMediaOutputs[currentOutputIndex];

  return (
    <div className={`jdv-container ${isMobile ? 'jdv-mobile' : ''}`}>
      {/* Compact Header */}
      <div className="jdv-header-compact">
        <div className="jdv-header-row">
          <span className="jdv-title">{modelName}</span>
          <button className="jdv-close-btn" onClick={onClose}>
            <CloseOutlined />
          </button>
        </div>
        <div className="jdv-job-id-row">
          <span className="jdv-job-id-text">{jobId}</span>
          <button 
            className="jdv-copy-btn" 
            onClick={() => copyToClipboard(jobId)}
            title="Copy Job ID"
          >
            <CopyOutlined />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="jdv-content jdv-scrollable">
        {/* Error */}
        {isFailed && errorInfo && <ErrorDisplay error={errorInfo} />}

        {/* Processing */}
        {isProcessing && (
          <div className="jdv-processing-state">
            <div className="jdv-processing-spinner" />
            <span>Processing...</span>
          </div>
        )}

        {/* Output Carousel - Hero position */}
        {isCompleted && allMediaOutputs.length > 0 && (
          <div className="jdv-output-hero">
            <div className="jdv-hero-image">
              <Swiper
                modules={[Pagination]}
                initialSlide={currentOutputIndex}
                spaceBetween={0}
                slidesPerView={1}
                pagination={{
                  clickable: true,
                }}
                loop={false}
                grabCursor={true}
                resistance={true}
                resistanceRatio={0.85}
                onSwiper={(swiper) => { swiperRef.current = swiper; }}
                onSlideChange={(swiper) => setCurrentOutputIndex(swiper.activeIndex)}
                className="jdv-hero-swiper"
              >
                {allMediaOutputs.map((output, idx) => (
                  <SwiperSlide key={idx}>
                    {output?.type === 'video' ? (
                      <video 
                        src={output.url} 
                        controls 
                        playsInline
                        controlsList="nodownload"
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain', 
                          background: '#000',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <div onClick={() => handleImageClick(output, imageOutputs)}>
                        <img src={output?.url} alt="Output" />
                      </div>
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
              
              {/* Arrow navigation */}
              {allMediaOutputs.length > 1 && (
                <>
                  <button 
                    className={`jdv-arrow jdv-arrow-left ${currentOutputIndex === 0 ? 'disabled' : ''}`}
                    onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                  >
                    <LeftOutlined />
                  </button>
                  <button 
                    className={`jdv-arrow jdv-arrow-right ${currentOutputIndex === allMediaOutputs.length - 1 ? 'disabled' : ''}`}
                    onClick={(e) => { e.stopPropagation(); goToNext(); }}
                  >
                    <RightOutlined />
                  </button>
                </>
              )}
            </div>

            {/* Quick actions under image */}
            <div className="jdv-hero-actions">
              {onEditImage && (
                <button className="jdv-hero-action" onClick={() => onEditImage(currentOutput?.url)}>
                  Edit
                </button>
              )}
              <button className="jdv-hero-action" onClick={() => downloadImage(currentOutput?.url, `output_${currentOutputIndex + 1}`)}>
                Download
              </button>
              {onReuseParams && data?.input_params && (
                <button className="jdv-hero-action" onClick={() => onReuseParams(data)}>
                  Re-use
                </button>
              )}
              {onToggleLike && (
                <button
                  className="jdv-hero-action"
                  style={{ color: liked ? '#e63946' : undefined, opacity: liking ? 0.6 : 1 }}
                  onClick={async () => {
                    if (liking) return;
                    setLiking(true);
                    setLiked(prev => !prev);
                    try {
                      const jobId = getJobId(data);
                      const jobType = data?.job_type || (data?.workflow_job_id ? 'workflow' : 'image');
                      const result = await onToggleLike(jobId, jobType);
                      if (result?.liked !== undefined) setLiked(result.liked);
                    } catch {
                      setLiked(prev => !prev);
                    } finally {
                      setLiking(false);
                    }
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  {liked ? 'Liked' : 'Like'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Prompts */}
        {Object.keys(prompts).length > 0 && (
          <PromptsSection prompts={prompts} isMobile={isMobile} />
        )}

        {/* Input Images */}
        {inputImages.length > 0 && (
          <InputsSection 
            images={inputImages} 
            onImageClick={handleImageClick}
            onEditImage={onEditImage}
            isMobile={isMobile}
          />
        )}

        {/* Metadata */}
        <div className="jdv-metadata-section">
          <div className="jdv-metadata-item">
            <span className="jdv-metadata-label">Status</span>
            <span className="jdv-metadata-value" style={{ color: statusColor }}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
          <div className="jdv-metadata-item">
            <span className="jdv-metadata-label">Created</span>
            <span className="jdv-metadata-value">{formatRelativeDate(data?.created_at)}</span>
          </div>
          {data?.duration && (
            <div className="jdv-metadata-item">
              <span className="jdv-metadata-label">Duration</span>
              <span className="jdv-metadata-value">{formatDuration(data.duration)}</span>
            </div>
          )}
          {data?.cost_to_client > 0 && (
            <div className="jdv-metadata-item">
              <span className="jdv-metadata-label">Cost</span>
              <span className="jdv-metadata-value jdv-cost">{formatCost(data.cost_to_client)}</span>
            </div>
          )}
        </div>

        {/* Parameters */}
        {data?.input_params && Object.keys(data.input_params).length > 0 && (
          <ParamsViewer 
            inputParams={data.input_params} 
            onReuseParams={onReuseParams}
            data={data}
            compact={true}
          />
        )}

        {/* Bottom Action - Reuse */}
        {onReuseParams && data?.input_params && (
          <div className="jdv-bottom-action">
            <Button
              type="primary"
              size="large"
              icon={<ReloadOutlined />}
              onClick={() => onReuseParams(data)}
              block
              className="jdv-reuse-btn"
            >
              Re-use Parameters
            </Button>
          </div>
        )}
      </div>

      {/* Image Viewer Modal - images only; video and audio are played inline */}
      <ImageViewerModal
        isOpen={!!fullViewerImage && fullViewerImage?.type !== 'video' && fullViewerImage?.type !== 'audio'}
        images={fullViewerImages.filter(img => img?.type !== 'video' && img?.type !== 'audio').map(img => img.url || img)}
        inputImages={inputImages.filter(img => img.type === 'image').map(img => img.url)}
        initialIndex={fullViewerImages.filter(img => img?.type !== 'video' && img?.type !== 'audio').findIndex(img => (img.url || img) === (fullViewerImage?.url || fullViewerImage))}
        onClose={() => setFullViewerImage(null)}
        onUseAsInput={onEditImage ? (url) => onEditImage(url) : null}
      />
    </div>
  );
};

export default JobDetailView;
